import { useCallback, useRef, useState } from "react";
import { CheckCircle2, Flashlight, ImagePlus, Keyboard, RotateCcw, XCircle } from "lucide-react";
import { Button, Card, CardBody, TextInput } from "@/ui";
import { KarigarShell } from "@/components/KarigarShell";
import { useQrScanner, type ScannerState } from "@/lib/useQrScanner";
import { extractCode, scanErrorText, submitScan, type ScanResult } from "@/lib/karigarPoints";
import { decodeQrImage } from "@/lib/decodeQrImage";
import { useLanguage } from "@/i18n/language";

/**
 * Scan a printed code and get the points — WIREFRAMES.md "Karigar scan".
 *
 * Camera, image upload, and manual entry are all available. The camera is
 * blocked outright on plain HTTP and absent on iPhones, and a smudged sticker
 * defeats any decoder, so the typed field is always on screen rather than
 * hidden behind a "having trouble" link. The printed code is built from
 * unambiguous characters precisely so that typing it is realistic.
 *
 * The camera is released the moment a code is read and stays off while the
 * result is up: a viewfinder running behind an answer drains the battery, and
 * re-reading the same sticker would post it twice.
 */
type Outcome =
  | { kind: "ok"; result: ScanResult }
  | { kind: "error"; hi: string; en: string };

/** What to say when the camera is unavailable, and whose problem it is. */
const CAMERA_NOTE: Record<
  Exclude<ScannerState, "running" | "starting">,
  { hi: string; en: string }
> = {
  insecure: {
    hi: "इस लिंक पर कैमरा नहीं चल सकता। नीचे कोड लिखें।",
    en: "The camera is blocked on this link. Type the code below instead.",
  },
  unsupported: {
    hi: "इस फ़ोन में कैमरा नहीं मिला। नीचे कोड लिखें।",
    en: "No camera was found on this device. Type the code below instead.",
  },
  denied: {
    hi: "कैमरे की अनुमति नहीं मिली। अनुमति दें, या नीचे कोड लिखें।",
    en: "Camera permission was refused. Allow it, or type the code below.",
  },
  error: {
    hi: "कैमरा चालू नहीं हो पाया। नीचे कोड लिखें।",
    en: "The camera would not start. Type the code below instead.",
  },
};

export function KarigarScanPage() {
  const { t, pick } = useLanguage("hi");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const imageInput = useRef<HTMLInputElement>(null);

  const send = useCallback(async (raw: string): Promise<void> => {
    setBusy(true);
    try {
      setOutcome({ kind: "ok", result: await submitScan(extractCode(raw)) });
    } catch (err) {
      setOutcome({ kind: "error", ...scanErrorText(err) });
    } finally {
      setBusy(false);
    }
  }, []);

  const scanner = useQrScanner({
    onCode: (raw) => void send(raw),
    enabled: outcome === null && !busy && !imageBusy,
  });

  async function readImage(file: File): Promise<void> {
    setImageBusy(true);
    setImageError(null);
    try {
      const code = await decodeQrImage(file);
      if (!code) {
        setImageError(t("तस्वीर में QR कोड नहीं मिला। साफ़ तस्वीर चुनें या कोड लिखें।", "No QR code found in the image. Choose a clearer image or type the code."));
      } else {
        await send(code);
      }
    } catch (error) {
      setImageError(error instanceof Error ? error.message : t("तस्वीर पढ़ी नहीं जा सकी।", "Could not read the image."));
    } finally {
      setImageBusy(false);
    }
  }

  function again(): void {
    setManual("");
    setOutcome(null);
    setImageError(null);
    scanner.retry();
  }

  const cameraNote =
    scanner.state === "running" || scanner.state === "starting"
      ? null
      : CAMERA_NOTE[scanner.state];

  return (
    <KarigarShell active="scan" title={t("कोड स्कैन करें", "Scan a code")}>
      {outcome ? (
        <ScanOutcome onAgain={again} outcome={outcome} />
      ) : (
        <>
      <Card>
        <CardBody className="flex flex-col gap-3">
          <p className="text-sm text-muted">{t("कोड को चौकोर के अंदर रखें", "Hold the code inside the square")}</p>

          <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-ink/90">
            {/* `playsInline` keeps iOS from taking the video fullscreen, and
                `muted` is what allows autoplay to start at all. */}
            <video className="size-full object-cover" muted playsInline ref={scanner.videoRef} />

            {cameraNote ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-ink/90 px-6 text-center">
                <Keyboard aria-hidden="true" className="mb-1 size-7 text-white/70" />
                <p className="text-sm font-medium text-white">{pick(cameraNote)}</p>
              </div>
            ) : (
              /* An aiming frame rather than a full overlay: it shows where to
                 put the sticker without hiding what the camera already sees. */
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-[18%] rounded-lg border-2 border-white/80"
              />
            )}

            {busy || imageBusy ? (
              <div className="absolute inset-0 flex items-center justify-center bg-ink/70">
                <p className="text-sm font-medium text-white">{t("जाँच हो रही है…", "Checking…")}</p>
              </div>
            ) : null}
          </div>

          {scanner.torchAvailable || scanner.state === "denied" ? (
            <div className="flex flex-wrap gap-2">
              {scanner.torchAvailable ? (
                <Button
                  icon={<Flashlight aria-hidden="true" className="size-4" />}
                  onClick={scanner.toggleTorch}
                  variant="secondary"
                >
                  {scanner.torchOn ? t("रोशनी बंद", "Light off") : t("रोशनी चालू", "Light on")}
                </Button>
              ) : null}
              {scanner.state === "denied" ? (
                <Button
                  icon={<RotateCcw aria-hidden="true" className="size-4" />}
                  onClick={scanner.retry}
                  variant="secondary"
                >
                  {t("दोबारा कोशिश करें", "Try again")}
                </Button>
              ) : null}
            </div>
          ) : null}
          <input
            accept="image/*"
            className="hidden"
            disabled={busy || imageBusy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) void readImage(file);
            }}
            ref={imageInput}
            type="file"
          />
          <Button disabled={busy || imageBusy} icon={<ImagePlus aria-hidden="true" className="size-4" />}
            onClick={() => imageInput.current?.click()} variant="secondary">
            {t("तस्वीर से कोड चुनें", "Upload QR image")}
          </Button>
          {imageError ? <p className="text-sm text-danger" role="alert">{imageError}</p> : null}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (manual.trim()) void send(manual);
            }}
          >
            <TextInput
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              hint={t("कोड कागज़ पर छपा होता है", "The code is printed on the label")}
              label={t("या कोड लिखें", "Or type the code")}
              onChange={(e) => setManual(e.target.value)}
              spellCheck={false}
              value={manual}
            />
            <Button block disabled={!manual.trim()} loading={busy} type="submit">
              {t("अंक लें", "Get points")}
            </Button>
          </form>
        </CardBody>
      </Card>
        </>
      )}
    </KarigarShell>
  );
}

function ScanOutcome({ outcome, onAgain }: { outcome: Outcome; onAgain: () => void }) {
  const { t } = useLanguage("hi");
  const ok = outcome.kind === "ok";

  return (
    <Card>
      <CardBody className="flex flex-col items-center gap-2 py-7 text-center">
        {ok ? (
          <CheckCircle2 aria-hidden="true" className="size-10 text-success" />
        ) : (
          <XCircle aria-hidden="true" className="size-10 text-danger" />
        )}

        {/* `role="status"` so the result is announced rather than only seen.
            This is the one moment on the screen that carries money. */}
        <div role="status">
          {outcome.kind === "ok" ? (
            <>
              <p className="font-title text-3xl font-semibold text-ink tabular-nums">
                +{outcome.result.points}
              </p>
              <p className="text-sm text-muted">{t("अंक मिले", "points added")}</p>
              <p className="mt-3 text-base text-ink">
                {t("कुल अंक", "Total points")}:{" "}
                <span className="font-semibold tabular-nums">{outcome.result.balance}</span>
              </p>
            </>
          ) : (
            <>
              <p className="text-base font-medium text-ink">{t(outcome.hi, outcome.en)}</p>
            </>
          )}
        </div>

        <Button block className="mt-3" onClick={onAgain}>
          {t("एक और स्कैन करें", "Scan another")}
        </Button>
      </CardBody>
    </Card>
  );
}
